import React from 'react';
import { Box, Typography } from '@mui/material';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import { PRESERVE_LINE_BREAKS_SX } from '../Events/ManageEvent/eventFormConstants';
import { APP_EVENT_PRIMARY } from '../Events/ManageEvent/eventLivePreviewModel';
import LivePreviewPhoneFrame from '../Events/ManageEvent/components/LivePreviewPhoneFrame';
import { displayPartnerStat } from './communityLivePreviewStats';

const InstagramIcon = () => (
  <Box
    component="svg"
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke={APP_EVENT_PRIMARY}
    strokeWidth="1.8"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill={APP_EVENT_PRIMARY} stroke="none" />
  </Box>
);

const displayWebsiteLabel = (url) =>
  String(url)
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/$/, '');

const STAT_CARDS = [
  { key: 'eventsHosted', label: 'Events Hosted', Icon: CalendarTodayOutlinedIcon },
  { key: 'activeSince', label: 'Active Since', Icon: ScheduleOutlinedIcon },
  { key: 'guestsAttended', label: 'Guests Attended', Icon: GroupsOutlinedIcon },
  { key: 'attendanceRate', label: 'Attendance Rate', Icon: ShowChartOutlinedIcon, suffix: '%' },
];

const CommunityLivePreview = ({ form, stats }) => {
  const name = form?.fullName?.trim() || 'Community Name';
  const short = form?.shortDescription?.trim() || '';
  const about = form?.description?.trim() || '';
  const cover = form?.coverPreview;
  const logo = form?.logoPreview;
  const initial = (name || 'C').charAt(0).toUpperCase();
  const statValues = stats && typeof stats === 'object' ? stats : {};
  const whatsappUrl = form?.whatsappUrl?.trim() || '';

  const connectLinks = [
    form?.websiteUrl?.trim()
      ? {
          key: 'website',
          label: displayWebsiteLabel(form.websiteUrl),
          icon: <LanguageOutlinedIcon sx={{ fontSize: 16 }} />,
        }
      : null,
    form?.instagramUrl?.trim()
      ? { key: 'instagram', label: 'Instagram', icon: <InstagramIcon /> }
      : null,
    form?.facebookUrl?.trim()
      ? {
          key: 'facebook',
          label: 'Facebook',
          icon: <FacebookOutlinedIcon sx={{ fontSize: 16 }} />,
        }
      : null,
  ].filter(Boolean);

  return (
    <LivePreviewPhoneFrame
      title="Live Preview"
      subtitle="How this community appears in the SureSpace app."
      footer="Updates live as you edit the form"
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: '#FFFFFF' }}>
        <Box
          sx={{
            height: 200,
            bgcolor: '#090B12',
            position: 'relative',
          }}
        >
          {cover ? (
            <Box
              component="img"
              key={cover}
              src={cover}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : null}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(9,11,18,0.35) 0%, rgba(9,11,18,0) 100%)',
            }}
          />
        </Box>

        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: '200px',
              bgcolor: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: '-32px',
              mb: 1.5,
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 82,
                height: 82,
                borderRadius: '200px',
                overflow: 'hidden',
                bgcolor: '#F2F1F3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B6A6E',
                fontWeight: 700,
                fontSize: 28,
              }}
            >
              {logo ? (
                <Box
                  component="img"
                  key={logo}
                  src={logo}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                initial
              )}
            </Box>
          </Box>

          <Typography
            sx={{
              color: '#1E1D20',
              fontWeight: 600,
              fontSize: 20,
              lineHeight: '26px',
              mb: 1,
            }}
          >
            {name}
          </Typography>

          {short ? (
            <Typography
              sx={{
                color: '#5A595E',
                fontSize: 13,
                lineHeight: '18px',
                mb: 2,
              }}
            >
              {short}
            </Typography>
          ) : (
            <Typography sx={{ color: '#B3B2B6', fontSize: 13, lineHeight: '18px', mb: 2 }}>
              Short description
            </Typography>
          )}

          {whatsappUrl ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25 }}>
              <Box
                aria-label="WhatsApp community"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  bgcolor: '#25D366',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFFFFF',
                  boxShadow: '0 3px 8px rgba(37, 211, 102, 0.35)',
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 19 }} />
              </Box>
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            {STAT_CARDS.map((item) => {
              const Icon = item.Icon;
              return (
                <Box
                  key={item.key}
                  sx={{
                    width: '48.5%',
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #EFE9EB',
                    borderRadius: '12px',
                    py: 1.25,
                    px: 1.25,
                    mb: 1.5,
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '16px',
                      bgcolor: '#FBE7E7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                      flexShrink: 0,
                      color: APP_EVENT_PRIMARY,
                    }}
                  >
                    <Icon sx={{ fontSize: 16 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#000000',
                        fontWeight: 600,
                        fontSize: 15,
                        lineHeight: '18px',
                      }}
                    >
                      {displayPartnerStat(statValues[item.key], item.suffix)}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#000000',
                        fontSize: 10,
                        lineHeight: '13px',
                        mt: 0.125,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ color: '#1E1D20', fontWeight: 600, fontSize: 14, mb: 1.25 }}>
            About the Host
          </Typography>
          <Box
            sx={{
              bgcolor: 'rgba(247, 148, 164, 0.15)',
              borderRadius: '12px',
              py: 1.5,
              px: 1.75,
            }}
          >
            <Typography
              sx={{
                color: about ? '#1E1D20' : '#B3B2B6',
                fontSize: 13,
                lineHeight: 1.45,
                ...PRESERVE_LINE_BREAKS_SX,
              }}
            >
              {about || 'About description'}
            </Typography>
          </Box>

          {connectLinks.length > 0 ? (
            <>
              <Typography
                sx={{ color: '#1E1D20', fontWeight: 600, fontSize: 14, mt: 2.25, mb: 1.25 }}
              >
                Connect
              </Typography>
              {connectLinks.map((link) => (
                <Box
                  key={link.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #EFE9EB',
                    borderRadius: '12px',
                    py: 1.25,
                    px: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '16px',
                      bgcolor: '#FBE7E7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                      flexShrink: 0,
                      color: APP_EVENT_PRIMARY,
                    }}
                  >
                    {link.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: '#1E1D20',
                      fontSize: 12,
                      lineHeight: '18px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {link.label}
                  </Typography>
                </Box>
              ))}
            </>
          ) : null}
        </Box>
      </Box>
    </LivePreviewPhoneFrame>
  );
};

export default CommunityLivePreview;
